import { useEffect, useState } from "react";
import { searchGroups } from "../groupsApi";
import { SEARCH_DEBOUNCE_MS } from "../groupsConstants";

/**
 * Inner async loader for search. Kept outside the hook to stay under 25 lines.
 * @param {Object} args - { query, signal, setResults, setError, setLoading }
 */
async function runSearch({ query, signal, setResults, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await searchGroups(query, { signal });
        if (!signal.aborted) setResults(Array.isArray(data) ? data : []);
    } catch (err) {
        if (signal.aborted) return;
        setError(err?.response?.data || "Recherche impossible");
        setResults([]);
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Custom hook returning a debounced search over the admin groups endpoint.
 * Returns an empty result list when the query is empty.
 * @returns {{ query: string, setQuery: Function, results: Array, loading: boolean, error: any, hasQuery: boolean }}
 */
function useGroupSearch() {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const trimmed = query.trim();
        if (!trimmed) {
            setResults([]);
            setLoading(false);
            setError(null);
            return undefined;
        }
        const controller = new AbortController();
        const timeout = setTimeout(() => {
            runSearch({ query: trimmed, signal: controller.signal, setResults, setError, setLoading });
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
    }, [query]);

    return { query, setQuery, results, loading, error, hasQuery: query.trim().length > 0 };
}

export default useGroupSearch;
